import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { TOKEN_NAME } from '@constants/global';
import { CookieService } from 'ngx-cookie-service';
import { UserStateService } from '@services/user-state.service';
import { AuthService } from '@services/auth.service';
import { map, catchError, of } from 'rxjs';
import { environment } from '@environments/environment';

const LOCAL_ONLY_ROUTES = new Set(['/labs', '/theme']);

export const authGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
	const cookieService = inject(CookieService);
	const router = inject(Router);
	const userStateService = inject(UserStateService);
	const authService = inject(AuthService);

	// 1. Verificar si hay token
	if (!cookieService.check(TOKEN_NAME)) {
		return router.createUrlTree(['/login'], {
			queryParams: { returnUrl: state.url },
		});
	}

	// 2. Si ya está cargada la info del usuario, validar acceso
	if (userStateService.isLoaded() && userStateService.userInfo()) {
		return validateRouteAccess(state.url, userStateService, router);
	}

	// 3. Cargar info del usuario y manejar errores
	return userStateService.loadUserInfo().pipe(
		map((result) => {
			if (result.success) {
				return validateRouteAccess(state.url, userStateService, router);
			}

			// Manejar diferentes tipos de error
			const error = result.error;

			if (error?.type === 'auth') {
				// Error de autenticación - limpiar sesión y redirigir a login
				authService.removeTokens(false);
				return router.createUrlTree(['/login'], {
					queryParams: { returnUrl: state.url },
				});
			}

			if (error?.type === 'user-not-found') {
				return router.createUrlTree(['/error'], {
					queryParams: {
						type: 'user-not-found',
						returnUrl: state.url,
					},
				});
			}

			if (error?.type === 'network' || error?.type === 'server') {
				// Error de red/servidor - mostrar página de error
				return router.createUrlTree(['/error'], {
					queryParams: {
						type: error.type,
						returnUrl: state.url,
					},
				});
			}

			// Error desconocido - redirigir a login por seguridad
			authService.removeTokens(false);
			return router.createUrlTree(['/login'], {
				queryParams: { returnUrl: state.url },
			});
		}),
		catchError(() => {
			// Error inesperado - redirigir a login
			authService.removeTokens(false);
			return of(
				router.createUrlTree(['/login'], {
					queryParams: { returnUrl: state.url },
				}),
			);
		}),
	);
};

/**
 * Valida si el usuario tiene acceso a la ruta actual
 */
function validateRouteAccess(
	url: string,
	userStateService: UserStateService,
	router: Router,
): boolean | import('@angular/router').UrlTree {
	const cleanUrl = url.split('?')[0].split('#')[0];
	const isLocalOnlyRoute =
		environment.appEnvironment === 'local' && LOCAL_ONLY_ROUTES.has(cleanUrl);

	if (isLocalOnlyRoute) {
		return true;
	}

	if (userStateService.hasAccessToRoute(cleanUrl)) {
		return true;
	}

	return router.createUrlTree(['/access-denied']);
}
